import { useControlledState } from "@react-stately/utils";
import { useEffect, useMemo, useRef, useState } from "react";
import { tv } from "tailwind-variants";

import { useDomRef } from "../../hooks/useDomRef";
import { cn } from "../../utils/cn";

import type { ComponentPropsWithoutRef, RefObject, TextareaHTMLAttributes } from "react";
import type { VariantProps } from "tailwind-variants";
import type { SlotsToClasses } from "../../utils/types";
import type { ButtonProps } from "../button/Button";
import type { ParagraphProps } from "../typography/Paragraph";

const editableParagraphVariants = tv({
  slots: {
    base: "flex flex-col gap-2",
    viewContainer: "relative",
    paragraph: [
      "cursor-pointer",
      "rounded",
      "border",
      "border-solid",
      "border-transparent",
      "px-2",
      "py-1",
      "pr-10",
      "transition-colors",
      "hover:border-gray-100",
      "dark:hover:border-gray-700",
      "whitespace-pre-wrap",
    ],
    textarea: [
      "w-full",
      "resize-none",
      "overflow-hidden",
      "rounded",
      "border",
      "border-gray-300",
      "bg-white",
      "p-4",
      "text-sm",
      "leading-5",
      "placeholder:text-gray-400",
      "focus:border-blue-500",
      "focus:outline-none",
      "focus:ring-1",
      "focus:ring-blue-500",
      "dark:border-gray-600",
      "dark:bg-gray-800",
      "dark:text-white",
    ],
    buttonGroup: "flex items-center gap-1",
    button: "shrink-0",
    editButton: "absolute right-0 top-0 shrink-0",
  },
});

type BaseProps = {
  /** Whether the text is currently being edited */
  isEditing?: boolean;
  /** Callback to set the editing state */
  setIsEditing?: (editing: boolean) => void;
  /** The default value of the text */
  defaultValue?: string;
  /** The current value of the text */
  value?: string;
  /** Callback to set the value of the text */
  onValueChange?: (value: string) => void;
  /** Callback when editing is submitted */
  onSubmit?: (value: string) => void;
  /** Additional props to pass to the textarea */
  textareaProps?: TextareaHTMLAttributes<HTMLTextAreaElement>;
  /** Additional props to pass to the button */
  buttonProps?: ButtonProps;
  /** Additional props to pass to the paragraph */
  paragraphProps?: ParagraphProps;
  /** Ref to the textarea element */
  textareaRef?: React.RefObject<HTMLTextAreaElement | null>;
  /** Optional classnames to pass to slots */
  classNames?: SlotsToClasses<keyof ReturnType<typeof editableParagraphVariants>>;
  /** Placeholder text for the textarea */
  placeholder?: string;
};

export type UseEditableParagraphProps = ComponentPropsWithoutRef<"div"> &
  BaseProps &
  VariantProps<typeof editableParagraphVariants>;

export function useEditableParagraph({
  className,
  classNames,
  children,
  isEditing: isEditingProp,
  setIsEditing: setIsEditingProp,
  value = typeof children === "string" ? children : undefined,
  defaultValue = "",
  onValueChange,
  onSubmit,
  textareaProps: textareaPropsProp,
  buttonProps: buttonPropsProp,
  paragraphProps: paragraphPropsProp,
  textareaRef,
  placeholder = "Add Note",
  ...props
}: UseEditableParagraphProps = {}) {
  const slots = useMemo(() => editableParagraphVariants(), []);

  const [isEditing, setIsEditing] = useControlledState(isEditingProp, false, setIsEditingProp);
  const [textValue, setTextValue] = useControlledState(value, defaultValue, onValueChange);

  const [draftValue, setDraftValue] = useState(textValue);

  const domRef = useDomRef(textareaRef);
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (domRef.current && isEditing) {
      domRef.current.style.height = "auto";
      domRef.current.style.height = `${domRef.current.scrollHeight}px`;
    }
  }, [draftValue, isEditing, domRef]);

  const baseProps = {
    ...props,
    className: slots.base({ className: cn(className, classNames?.base) }),
  };

  const textareaProps: TextareaHTMLAttributes<HTMLTextAreaElement> = {
    ...textareaPropsProp,
    autoFocus: true,
    value: draftValue,
    placeholder,
    rows: 3,
    onChange: e => {
      setDraftValue(e.target.value);
      textareaPropsProp?.onChange?.(e);
    },
    className: slots.textarea({
      className: cn(classNames?.textarea, textareaPropsProp?.className),
    }),
    style: {
      minHeight: "88px",
      ...textareaPropsProp?.style,
    },
    onBlur: e => {
      if (e.relatedTarget !== cancelButtonRef.current) {
        setTextValue(draftValue);
        setIsEditing(false);
        onSubmit?.(draftValue);
      }
      textareaPropsProp?.onBlur?.(e);
    },
    onKeyDown: e => {
      if (e.key === "Escape") {
        setDraftValue(textValue);
        setIsEditing(false);
      }
      // Note: Don't handle Enter for multi-line textarea
      textareaPropsProp?.onKeyDown?.(e);
    },
  };

  const baseButtonProps: ButtonProps = {
    ...buttonPropsProp,
    variant: "link",
    ghost: true,
    isIconOnly: true,
    className: slots.button({ className: cn(classNames?.button, buttonPropsProp?.className) }),
  };

  const editButtonProps: ButtonProps = {
    ...baseButtonProps,
    "aria-label": "Edit",
    className: slots.editButton({
      className: cn(classNames?.editButton, buttonPropsProp?.className),
    }),
    onClick: () => {
      setDraftValue(textValue);
      setIsEditing(true);
    },
  };

  const confirmButtonProps: ButtonProps = {
    ...baseButtonProps,
    "aria-label": "Confirm",
    onClick: () => {
      setTextValue(draftValue);
      setIsEditing(false);
      onSubmit?.(draftValue);
    },
  };

  const cancelButtonProps: ButtonProps & { ref: RefObject<HTMLButtonElement | null> } = {
    ...baseButtonProps,
    ref: cancelButtonRef,
    "aria-label": "Cancel",
    variant: "light",
    onClick: () => {
      setDraftValue(textValue);
      setIsEditing(false);
    },
  };

  const paragraphProps: ParagraphProps = {
    ...paragraphPropsProp,
    className: slots.paragraph({
      className: cn(classNames?.paragraph, paragraphPropsProp?.className),
    }),
    onClick: () => {
      setDraftValue(textValue);
      setIsEditing(true);
    },
  };

  const buttonGroupProps = {
    className: slots.buttonGroup({ className: classNames?.buttonGroup }),
  };

  const viewContainerProps = {
    className: slots.viewContainer({ className: classNames?.viewContainer }),
  };

  return {
    isEditing,
    textValue,
    baseProps,
    textareaProps,
    textareaRef: domRef,
    editButtonProps,
    confirmButtonProps,
    cancelButtonProps,
    paragraphProps,
    buttonGroupProps,
    viewContainerProps,
  };
}
